import { FormEvent, useCallback, useEffect, useState } from "react";

import { deleteProjectIssuance } from "../../../../service/project";
import { searchIssuanceTransactions } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../service/ValidationErrorFormatter";
import { useIsLoadingWrapper } from "../../../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../../../widget";

interface UseDeleteIssuancePanelProps {
  onClose: () => void;
  rowVersion?: number;
  issuanceUuid?: string;
  handleRefresh: () => void;
}

interface UseDeleteIssuancePanelReturnData {
  handleDeleteIssuance: (e: FormEvent<HTMLFormElement>) => void;
  handleClosePanel: () => void;
  errors?: ServiceError[];
  isHandleDeleteIssuanceLoading?: boolean;
  showIssuanceTransactionWarning: boolean;
}

export const useDeleteIssuancePanel = ({
  onClose,
  issuanceUuid,
  rowVersion,
  handleRefresh,
}: UseDeleteIssuancePanelProps): UseDeleteIssuancePanelReturnData => {
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [isHandleDeleteIssuanceLoading, setIsHandleDeleteIssuanceLoading] = useState(false);
  const [showIssuanceTransactionWarning, setShowIssuanceTransactionWarning] = useState(false);

  const handleClosePanel = (): void => {
    onClose();
    setShowIssuanceTransactionWarning(false);
  };

  const checkIssuanceTransactions = useCallback(
    async (uuid: string): Promise<boolean> => {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.issuanceUuid = {
        operator: "eq",
        value: uuid,
      };

      const res = await searchIssuanceTransactions({
        paging: {
          limit: 1,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: { results: filterCriteria },
      });

      if (res.status === Status.Success && res.data?.results.length && res.data.results.length > 0) {
        setShowIssuanceTransactionWarning(true);
        return true;
      }
      return false;
    },
    [issuanceUuid]
  );

  const handleDeleteIssuance = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);
    if (issuanceUuid && rowVersion) {
      const response = await deleteProjectIssuance({
        rowVersion,
        uuid: issuanceUuid,
      });
      if (response.status === Status.Success) {
        Toast.success({ message: "Issuance successfully deleted" });

        handleRefresh();
        onClose();
      }
      if (response.status === Status.Error && response.errors) {
        setErrors(response.errors.map((error) => ({ ...error, message: getErrorMessageFromCode(error.code) })));
      }
    }
  }, setIsHandleDeleteIssuanceLoading);

  useEffect(() => {
    (async () => {
      if (issuanceUuid) setShowIssuanceTransactionWarning(await checkIssuanceTransactions(issuanceUuid));
    })();
  }, [issuanceUuid]);

  return {
    handleDeleteIssuance,
    handleClosePanel,
    errors,
    isHandleDeleteIssuanceLoading,
    showIssuanceTransactionWarning,
  };
};
